import { TaxonomyCategory, TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faLock, faPlus, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { categoryLabel } from '../GovernanceTaxonomyManagement.utils';
import { ITaxonomySelectionGroup, ITaxonomySelectionRow } from '../taxonomySelection.utils';
import { GovernanceAiSuggestionLabel } from '../Onboarding/components/GovernanceOnboardingPrimitives';

const GRADIENT = 'linear-gradient(135deg, #009EE3 0%, #7491ED 34%, #D96ECC 68%, #FF5479 100%)';

export interface IGovernanceTaxonomySelectionProps {
  groups: ITaxonomySelectionGroup[];
  onAccept: (facet: TaxonomyFacet) => void;
  onIgnore: (facet: TaxonomyFacet) => void;
  onRemove: (facet: TaxonomyFacet) => void;
  /** Renders AI labels, confidence badges, and reasons on recommended rows. */
  showRecommendationInfo: boolean;
  /** Shows every facet without "Show all" links (settings modal mode). */
  expandAllCategories: boolean;
}

function RowLabel({ row, badge }: { row: ITaxonomySelectionRow; badge?: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        <FontAwesomeIcon icon={row.icon} className="shrink-0 text-slate-400" />
        <span className="text-sm font-bold text-slate-900 truncate">{row.name}</span>
        {badge}
      </div>
      <div className="mt-1 text-sm text-slate-500">{row.description}</div>
    </div>
  );
}

function MetaBadge({ children, tone }: { children: React.ReactNode; tone: 'neutral' | 'success' }) {
  const toneClasses =
    tone === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-600' : 'border-slate-200 bg-slate-100 text-slate-500';

  return (
    <span
      className={`inline-flex shrink-0 whitespace-nowrap rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${toneClasses}`}
    >
      {children}
    </span>
  );
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  return (
    <span className="inline-flex shrink-0 whitespace-nowrap rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">
      {confidence}%
    </span>
  );
}

function IconButton({
  onClick,
  title,
  icon,
  variant,
}: {
  onClick: () => void;
  title: string;
  icon: IconDefinition;
  variant: 'primary' | 'ghost';
}) {
  const variantClasses =
    variant === 'primary' ? 'bg-primary text-white hover:opacity-90' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-50';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`flex h-8 w-8 items-center justify-center rounded-lg ${variantClasses}`}
    >
      <FontAwesomeIcon icon={icon} />
    </button>
  );
}

function ConfirmDeleteButton({ onDelete }: { onDelete: () => void }) {
  const [pending, setPending] = React.useState(false);

  return (
    <button
      type="button"
      onClick={() => (pending ? onDelete() : setPending(true))}
      className="relative flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:text-red-500"
      title={pending ? 'Click again to confirm removal' : 'Remove'}
    >
      <span className={pending ? '-rotate-12 inline-flex' : 'inline-flex'}>
        <FontAwesomeIcon icon={faTrash} />
      </span>
      {pending ? <span className="absolute -right-1 -top-1 text-[10px] font-bold text-red-500">?</span> : null}
    </button>
  );
}

/**
 * Lays a row out with flexbox (not a viewport-based grid) so it stays correct at any
 * container width — the settings modal iframe can be far narrower than a `md:` breakpoint.
 * The description block takes half the width when a reasoning column is present and the
 * full width otherwise; actions only take what they need.
 */
function RowFrame({
  className,
  leftBar,
  label,
  reasoning,
  actions,
}: {
  className: string;
  leftBar?: boolean;
  label: React.ReactNode;
  reasoning?: React.ReactNode;
  actions: React.ReactNode;
}) {
  const hasReasoning = !!reasoning;

  return (
    <div className={`relative flex items-start gap-4 overflow-hidden p-4 ${className}`}>
      {leftBar ? <div className="absolute inset-y-0 left-0 w-1" style={{ background: GRADIENT }} /> : null}
      <div className={hasReasoning ? 'min-w-0 basis-1/2 shrink-0 grow-0' : 'min-w-0 flex-1'}>{label}</div>
      {hasReasoning ? <div className="min-w-0 flex-1">{reasoning}</div> : null}
      <div className="flex shrink-0 items-center gap-2 pl-1">{actions}</div>
    </div>
  );
}

function SelectionRow({
  row,
  showRecommendationInfo,
  onAccept,
  onIgnore,
  onRemove,
}: {
  row: ITaxonomySelectionRow;
  showRecommendationInfo: boolean;
  onAccept: (facet: TaxonomyFacet) => void;
  onIgnore: (facet: TaxonomyFacet) => void;
  onRemove: (facet: TaxonomyFacet) => void;
}) {
  if (row.state === 'recommended') {
    const reasoning = showRecommendationInfo ? (
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <GovernanceAiSuggestionLabel>Recommended</GovernanceAiSuggestionLabel>
          {typeof row.displayedConfidence === 'number' ? <ConfidenceBadge confidence={row.displayedConfidence} /> : null}
        </div>
        {row.reason ? <div className="mt-1 text-sm text-slate-700">{row.reason}</div> : null}
      </div>
    ) : undefined;

    return (
      <RowFrame
        className="rounded-l-sm rounded-r-xl border border-slate-200 bg-slate-50 shadow-sm"
        leftBar
        label={<RowLabel row={row} />}
        reasoning={reasoning}
        actions={
          <>
            <IconButton onClick={() => onAccept(row.facet)} title="Accept" icon={faCheck} variant="primary" />
            <IconButton onClick={() => onIgnore(row.facet)} title="Ignore" icon={faXmark} variant="ghost" />
          </>
        }
      />
    );
  }

  if (row.state === 'available') {
    return (
      <RowFrame
        className="rounded-xl border border-dashed border-slate-300 bg-white"
        label={<RowLabel row={row} />}
        reasoning={row.wasIgnored ? <div className="text-sm text-slate-500">Previously ignored</div> : undefined}
        actions={<IconButton onClick={() => onAccept(row.facet)} title="Add" icon={faPlus} variant="ghost" />}
      />
    );
  }

  if (row.state === 'required-missing') {
    // Required but not yet created (e.g. a newly introduced required facet on an existing
    // project). Show it as required, but let the user add it instead of locking it out.
    return (
      <RowFrame
        className="rounded-xl border border-slate-200 bg-white shadow-sm"
        label={<RowLabel row={row} badge={<MetaBadge tone="neutral">Required</MetaBadge>} />}
        actions={<IconButton onClick={() => onAccept(row.facet)} title="Add" icon={faPlus} variant="primary" />}
      />
    );
  }

  if (row.state === 'locked' || row.state === 'existing') {
    const locked = row.state === 'locked';

    return (
      <RowFrame
        className="rounded-xl border border-slate-200 bg-white shadow-sm"
        label={
          <RowLabel
            row={row}
            badge={locked ? <MetaBadge tone="neutral">Required</MetaBadge> : <MetaBadge tone="success">Added</MetaBadge>}
          />
        }
        actions={
          <span
            className="flex h-8 w-8 items-center justify-center pr-1 text-slate-400"
            title={locked ? 'This taxonomy is required and cannot be removed.' : 'Already configured'}
          >
            <FontAwesomeIcon icon={locked ? faLock : faCheck} />
          </span>
        }
      />
    );
  }

  return (
    <RowFrame
      className="rounded-xl border border-slate-200 bg-white shadow-sm"
      label={<RowLabel row={row} />}
      actions={<ConfirmDeleteButton onDelete={() => onRemove(row.facet)} />}
    />
  );
}

export function GovernanceTaxonomySelection({
  groups,
  onAccept,
  onIgnore,
  onRemove,
  showRecommendationInfo,
  expandAllCategories,
}: IGovernanceTaxonomySelectionProps) {
  const [showAllByCategory, setShowAllByCategory] = React.useState<Partial<Record<TaxonomyCategory, boolean>>>({});

  return (
    <div className="space-y-8">
      {groups.map((group) => {
        const expanded = expandAllCategories || !!showAllByCategory[group.category];
        const hasHiddenRows = group.showAllRows.length > 0 && !expanded;
        const showAllLink = (
          <button
            type="button"
            className="text-sm font-semibold text-primary hover:underline"
            onClick={() =>
              setShowAllByCategory((prev) => ({
                ...prev,
                [group.category]: !prev[group.category],
              }))
            }
          >
            {expanded ? 'Show fewer' : `Show all (${group.showAllRows.length})`}
          </button>
        );

        return (
          <div key={group.category} className="space-y-3">
            <h3 className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">{categoryLabel(group.category)}</h3>

            {group.rows.map((row) => (
              <SelectionRow
                key={row.facet}
                row={row}
                showRecommendationInfo={showRecommendationInfo}
                onAccept={onAccept}
                onIgnore={onIgnore}
                onRemove={onRemove}
              />
            ))}

            {expanded
              ? group.showAllRows.map((row) => (
                  <SelectionRow
                    key={row.facet}
                    row={row}
                    showRecommendationInfo={showRecommendationInfo}
                    onAccept={onAccept}
                    onIgnore={onIgnore}
                    onRemove={onRemove}
                  />
                ))
              : null}

            {!group.hasVisibleRows && hasHiddenRows ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-400">None recommended.</span>
                {showAllLink}
              </div>
            ) : hasHiddenRows || (expanded && !expandAllCategories && group.showAllRows.length > 0) ? (
              <div className="text-left">{showAllLink}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
